import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    Wallet,
    Clock,
    Lock,
    Database,
    CreditCard,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) => (cents == null ? "—" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * Side-by-side reconciliation between our internal `revenue_events` ledger
 * and live Stripe data for a configurable window (default: last 30 days).
 *
 * Surfaces the 3 questions every gym owner asks at month-end:
 *   • Did Stripe pay me what I'm owed? (balance: available / pending / reserved)
 *   • Does my dashboard match Stripe? (delta column highlights any gap)
 *   • Where are the missing dollars? (charges in Stripe but not in our DB)
 *
 * Designed to fail soft — if Stripe isn't reachable (preview env, bad key,
 * rate limit), the widget shows a yellow notice and still renders the
 * internal column so the owner can see at least their own numbers.
 */
export default function StripeReconcileWidget({ initialDays = 30 }) {
    const [days, setDays] = useState(initialDays);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/accounting/stripe-reconcile`, {
                params: { days }, withCredentials: true,
            });
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load Stripe reconciliation", {
                description: e?.response?.data?.detail || "Check your Stripe API key in prod",
            });
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const runBackfill = async () => {
        setBackfilling(true);
        try {
            const r = await axios.post(
                `${API}/coach/accounting/backfill-tax-and-fees`,
                {}, { withCredentials: true },
            );
            const d = r.data;
            toast.success("Backfill complete", {
                description: `Fixed ${d.tax_fixed} tax + ${d.fee_fixed} fee entries (recovered $${(d.tax_cents_recovered/100).toFixed(2)} tax, $${(d.fee_cents_recovered/100).toFixed(2)} fees)`,
                duration: 8000,
            });
            await load();
        } catch (e) {
            toast.error("Backfill failed", { description: e?.response?.data?.detail });
        } finally {
            setBackfilling(false);
        }
    };

    const [pullingCharges, setPullingCharges] = useState(false);
    const pullMissingCharges = async () => {
        if (!window.confirm(`Pull ${data?.stripe?.missing_in_ledger_count || 0} missing charge(s) from Stripe into the ledger? Idempotent — safe to re-run.`)) return;
        setPullingCharges(true);
        try {
            const r = await axios.post(
                `${API}/coach/accounting/backfill`,
                {}, {
                    params: { days: Math.min(days, 365) },
                    withCredentials: true,
                },
            );
            const c = r.data?.counts || {};
            toast.success("Missing charges pulled", {
                description: `Imported ${c.charges || 0} charges, ${c.refunds || 0} refunds, ${c.disputes || 0} disputes`,
                duration: 8000,
            });
            await load();
        } catch (e) {
            toast.error("Charge backfill failed", { description: e?.response?.data?.detail });
        } finally {
            setPullingCharges(false);
        }
    };

    if (loading && !data) {
        return (
            <div data-testid="stripe-reconcile-loading" className="border border-zinc-800 bg-zinc-950 rounded-xl p-6 animate-pulse">
                <div className="h-5 bg-zinc-900 rounded w-1/3 mb-4" />
                <div className="grid grid-cols-3 gap-3">
                    <div className="h-20 bg-zinc-900 rounded" />
                    <div className="h-20 bg-zinc-900 rounded" />
                    <div className="h-20 bg-zinc-900 rounded" />
                </div>
            </div>
        );
    }
    if (!data) return null;

    const internal = data.internal || {};
    const stripe = data.stripe || {};
    const delta = data.delta || {};
    const stripeAvail = stripe.available;
    const missingTotal = stripe.missing_in_ledger_total_cents || 0;
    const grossDelta = delta.gross_cents || 0;
    const hasDelta = stripeAvail && (Math.abs(grossDelta) > 1000 || missingTotal > 0); // >$10 or any missing charges

    return (
        <div
            data-testid="stripe-reconcile-widget"
            className="border border-zinc-800 bg-gradient-to-br from-zinc-950 to-black rounded-xl shadow-lg overflow-hidden"
        >
            {/* Header */}
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold">Stripe ↔ Ledger Reconciliation</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                        Last {days} days · {hasDelta
                            ? <span className="text-amber-400 font-semibold">⚠ delta detected</span>
                            : <span className="text-emerald-400 font-semibold">in sync</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        data-testid="stripe-reconcile-days"
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs rounded px-2 py-1.5"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last 365 days</option>
                    </select>
                    <button
                        type="button"
                        data-testid="stripe-reconcile-refresh"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded px-2.5 py-1.5 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stripe-unreachable banner */}
            {!stripeAvail && (
                <div data-testid="stripe-reconcile-offline" className="px-5 py-3 bg-amber-950/40 border-b border-amber-900/40 text-xs text-amber-300 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                        <strong>Stripe API unreachable</strong> — {stripe.error || "API key not configured."} The internal ledger below is still accurate, but live deltas need a real Stripe key.
                    </div>
                </div>
            )}

            {/* Stripe Balance — where's the money */}
            {stripeAvail && (
                <div className="px-5 py-4 bg-zinc-900/40 border-b border-zinc-800">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-2">Stripe Balance — your actual money position</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <BalanceTile
                            icon={Wallet}
                            label="Available for payout"
                            value={fmt(stripe.balance_available_cents)}
                            tone="emerald"
                            testid="stripe-reconcile-available"
                        />
                        <BalanceTile
                            icon={Clock}
                            label="Pending (clearing 2-7 days)"
                            value={fmt(stripe.balance_pending_cents)}
                            tone="amber"
                            testid="stripe-reconcile-pending"
                            hint={stripe.balance_pending_cents > 100000 ? "This is normal — Stripe holds new funds before payout" : null}
                        />
                        <BalanceTile
                            icon={Lock}
                            label="Reserved / on hold"
                            value={fmt(stripe.balance_reserved_cents)}
                            tone={stripe.balance_reserved_cents > 0 ? "red" : "zinc"}
                            testid="stripe-reconcile-reserved"
                            hint={stripe.balance_reserved_cents > 0 ? "Stripe is holding funds — check email from risk team" : null}
                        />
                    </div>
                </div>
            )}

            {/* Side-by-side reconciliation */}
            <div className="px-5 py-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-3">
                    Activity window — Internal ledger vs Stripe
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="stripe-reconcile-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                                <th className="text-left py-2 font-semibold">Metric</th>
                                <th className="text-right py-2 font-semibold">Our ledger</th>
                                {stripeAvail && <th className="text-right py-2 font-semibold">Stripe (live)</th>}
                                {stripeAvail && <th className="text-right py-2 font-semibold">Delta</th>}
                            </tr>
                        </thead>
                        <tbody className="text-zinc-200">
                            <ReconRow label="Gross collected" us={internal.gross_cents} them={stripe.gross_cents} stripeAvail={stripeAvail} testid="row-gross" />
                            <ReconRow label="WA sales tax" us={internal.tax_cents} them={null} stripeAvail={stripeAvail} testid="row-tax" />
                            <ReconRow label="Stripe fees" us={internal.fee_cents} them={stripe.fee_cents} stripeAvail={stripeAvail} testid="row-fees" />
                            <ReconRow label="Refunds" us={internal.refund_cents} them={stripe.refund_cents} stripeAvail={stripeAvail} testid="row-refunds" tone="muted" />
                            <ReconRow label="Net (gross − tax − fees)" us={internal.net_cents} them={stripe.net_cents} stripeAvail={stripeAvail} testid="row-net" emphasis />
                            <tr className="text-xs text-zinc-500">
                                <td className="py-2">Charge count</td>
                                <td className="text-right py-2 font-mono">{internal.n}</td>
                                {stripeAvail && <td className="text-right py-2 font-mono">{stripe.charge_count}</td>}
                                {stripeAvail && <td className="text-right py-2 font-mono">
                                    {(stripe.charge_count - internal.n) > 0
                                        ? <span className="text-amber-400">+{stripe.charge_count - internal.n}</span>
                                        : <span className="text-zinc-600">0</span>}
                                </td>}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Missing charges */}
            {stripeAvail && stripe.missing_in_ledger_count > 0 && (
                <div data-testid="stripe-reconcile-missing" className="px-5 py-4 bg-red-950/30 border-t border-red-900/40">
                    <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="text-sm font-semibold text-red-200">
                                {stripe.missing_in_ledger_count} Stripe charge{stripe.missing_in_ledger_count > 1 ? "s" : ""} ({fmt(missingTotal)}) not in our ledger
                            </div>
                            <div className="text-xs text-red-300/80 mt-0.5">
                                These charges hit Stripe but never wrote to our DB — webhook gap. Run the reconcile backfill to pull them in.
                            </div>
                        </div>
                    </div>
                    <details className="text-xs">
                        <summary className="cursor-pointer text-red-300 hover:text-red-200 mb-2">Show missing charges</summary>
                        <div className="space-y-1 mt-2">
                            {stripe.missing_in_ledger.map((m) => (
                                <div key={m.charge_id} className="flex items-center justify-between gap-2 font-mono text-[11px] text-zinc-300 bg-black/30 rounded px-2 py-1.5">
                                    <span className="text-zinc-500">{new Date(m.created * 1000).toLocaleDateString()}</span>
                                    <span className="flex-1 truncate text-zinc-300">{m.description || m.charge_id}</span>
                                    <span className="text-red-300 font-semibold">{fmt(m.amount_cents)}</span>
                                </div>
                            ))}
                        </div>
                    </details>
                    <button
                        type="button"
                        data-testid="stripe-reconcile-pull-missing"
                        onClick={pullMissingCharges}
                        disabled={pullingCharges}
                        className="mt-3 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-xs font-semibold tracking-wider uppercase rounded px-3 py-1.5"
                    >
                        <Database className="w-3.5 h-3.5" />
                        {pullingCharges ? "Pulling…" : `Pull ${stripe.missing_in_ledger_count} missing charge${stripe.missing_in_ledger_count > 1 ? "s" : ""} into ledger`}
                    </button>
                </div>
            )}

            {/* In-sync footer */}
            {stripeAvail && !hasDelta && (
                <div data-testid="stripe-reconcile-clean" className="px-5 py-3 bg-emerald-950/20 border-t border-emerald-900/40 text-xs text-emerald-300 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Internal ledger matches Stripe for this window. {data.tax_remittance_owed_cents > 0 && (
                        <span className="ml-1 text-emerald-200">Tax to remit to WA Dept of Revenue: <strong>{fmt(data.tax_remittance_owed_cents)}</strong></span>
                    )}
                </div>
            )}

            {/* Action footer */}
            <div className="px-5 py-3 bg-black/40 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap text-xs">
                <div className="flex items-center gap-2 text-zinc-500">
                    <Database className="w-3.5 h-3.5" />
                    {data.tax_remittance_owed_cents > 0 && (
                        <span>Tax owed to WA: <span className="text-zinc-200 font-mono">{fmt(data.tax_remittance_owed_cents)}</span></span>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="stripe-reconcile-backfill-btn"
                    onClick={runBackfill}
                    disabled={backfilling}
                    className="inline-flex items-center gap-1.5 bg-brand-red hover:bg-brand-red/90 disabled:opacity-60 text-white text-xs font-semibold tracking-wider uppercase rounded px-3 py-1.5"
                >
                    <CreditCard className="w-3.5 h-3.5" />
                    {backfilling ? "Running…" : "Backfill tax & fees"}
                </button>
            </div>
        </div>
    );
}

function BalanceTile({ icon: Icon, label, value, tone = "zinc", hint, testid }) {
    const tones = {
        emerald: "border-emerald-900/40 bg-emerald-950/20 text-emerald-200",
        amber:   "border-amber-900/40 bg-amber-950/20 text-amber-200",
        red:     "border-red-900/40 bg-red-950/20 text-red-200",
        zinc:    "border-zinc-800 bg-zinc-900/40 text-zinc-200",
    };
    return (
        <div data-testid={testid} className={`border ${tones[tone]} rounded-lg px-3 py-2.5`}>
            <div className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase opacity-80 mb-1">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="text-xl font-mono font-semibold">{value}</div>
            {hint && <div className="text-[10px] opacity-70 mt-1">{hint}</div>}
        </div>
    );
}

function ReconRow({ label, us, them, stripeAvail, testid, emphasis, tone }) {
    const delta = stripeAvail && them != null && us != null ? them - us : null;
    const deltaTone = delta == null || Math.abs(delta) < 100
        ? "text-zinc-600"
        : delta > 0
            ? "text-amber-400"
            : "text-emerald-400";
    return (
        <tr data-testid={testid} className={`border-b border-zinc-900 ${emphasis ? "font-semibold" : ""} ${tone === "muted" ? "text-zinc-500" : ""}`}>
            <td className="py-2">{label}</td>
            <td className="text-right py-2 font-mono">{fmt(us)}</td>
            {stripeAvail && <td className="text-right py-2 font-mono">{them == null ? <span className="text-zinc-700">n/a</span> : fmt(them)}</td>}
            {stripeAvail && <td className={`text-right py-2 font-mono ${deltaTone}`}>
                {delta == null
                    ? "—"
                    : delta === 0
                        ? "·"
                        : `${delta > 0 ? "+" : ""}${fmt(delta)}`}
            </td>}
        </tr>
    );
}
